import * as THREE from 'three'
import type { VisualParams } from '../../types'
import type { SharedUniforms } from '../AudioUniforms'
import { PRIMITIVE_TEXTURE_GLSL } from '../AudioUniforms'
import { BasePrimitive } from './Primitive'
import { hsv2rgb, snoise, ZONE_GLSL_COMMON, ZONE_GLSL_FRAG } from '../shaderChunks'

/**
 * Triangle mesh (icosphere) that deforms along its normals with noise + bass,
 * and vibrates with the spectrum. Rendered with visible faces + rim.
 */
export class TriangleField extends BasePrimitive {
  object3D: THREE.Mesh
  private material: THREE.ShaderMaterial

  constructor(uniforms: SharedUniforms) {
    super(uniforms)
    this.material = new THREE.ShaderMaterial({
      uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
      transparent: true,
      side: THREE.DoubleSide,
      vertexShader: /* glsl */ `
        attribute float aFreqIndex;
        uniform float uTime, uBass, uMid, uBeat, uReactivity, uBassPunch;
        uniform vec3 uSeed;
        uniform float uSeedPulse;
        uniform sampler2D uFreq;
        varying float vDisp;
        varying vec3 vNormalW;
        varying vec3 vViewDir;
        varying vec3 vDir;
        ${snoise}
        ${ZONE_GLSL_COMMON}
        void main() {
          float mag = texture2D(uFreq, vec2(aFreqIndex, 0.5)).r;
          vec3 dir = normalize(position);
          vDir = dir;
          float n = snoise(normalize(position) * 1.8 + uTime * 0.25);
          float disp = ((n * 0.5 + 0.5) * (uMid + 0.2) * 1.2
                        + mag * 0.9
                        + uBass * uBassPunch
                        + uBeat * 0.6) * uReactivity;
          // Seed-driven beat punch only (uBeat decays to 0 between beats, so the
          // base shape stays original); the seed sets which faces pop out.
          float lobe = snoise(normalize(position) * 2.2 + uSeed + 13.0);
          disp += lobe * uSeedPulse * uReactivity * 1.0;
          // When zone reaction is on, freeze the base deform — only zones react.
          disp *= (1.0 - uZoneEnabled);
          disp += zoneDisp(dir);
          // Always-on idle wave so the faces never look fully static.
          disp += (sin(dot(dir, vec3(2.7, 3.3, 2.1)) + uTime * 0.7)
                 + sin(dot(dir, vec3(3.9, 1.7, 3.1)) - uTime * 0.5)) * 0.04;
          vDisp = disp;
          vec3 pos = position + normal * disp;
          vec4 mv = modelViewMatrix * vec4(pos, 1.0);
          vNormalW = normalize(normalMatrix * normal);
          vViewDir = normalize(-mv.xyz);
          gl_Position = projectionMatrix * mv;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        ${PRIMITIVE_TEXTURE_GLSL}
        uniform float uHue, uTreble, uLevel;
        varying float vDisp;
        varying vec3 vNormalW;
        varying vec3 vViewDir;
        varying vec3 vDir;
        ${hsv2rgb}
        ${ZONE_GLSL_COMMON}
        ${ZONE_GLSL_FRAG}
        void main() {
          float fresnel = pow(1.0 - max(dot(vNormalW, vViewDir), 0.0), 2.0);
          float hue = fract(uHue + vDisp * 0.2 + uTreble * 0.1);
          vec3 base = hsv2rgb(vec3(hue, 0.7, 0.35 + vDisp * 0.4));
          vec3 col = base + fresnel * 0.6;
          float alpha = 0.5 + fresnel * 0.5 + uLevel * 0.1;
          // Zone reaction: crisp shape decal on the mesh surface.
          vec4 z = zoneColor(vDir);
          vec3 outc = mix(applyPrimitiveTexture(col), z.rgb, z.a);
          gl_FragColor = vec4(outc, max(alpha, z.a));
        }
      `,
    })
    this.object3D = new THREE.Mesh(new THREE.IcosahedronGeometry(2, 8), this.material)
    this.rebuild({ density: 1 } as VisualParams)
  }

  rebuild(params: VisualParams): void {
    const detail = Math.max(2, Math.min(7, Math.round(6 * (params.density ?? 1))))
    const geo = new THREE.IcosahedronGeometry(2, detail)
    // aFreqIndex derived from the vertex height.
    const pos = geo.getAttribute('position')
    const freqIndex = new Float32Array(pos.count)
    for (let i = 0; i < pos.count; i++) {
      freqIndex[i] = Math.abs(pos.getY(i)) / 2
    }
    geo.setAttribute('aFreqIndex', new THREE.BufferAttribute(freqIndex, 1))
    this.object3D.geometry.dispose()
    this.object3D.geometry = geo
  }
}
